import { useEffect, useCallback } from 'react';
import { RefreshCw, CheckCircle2, AlertCircle, Ban, Download, ArrowDownToLine } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Progress } from '../../../../components/ui/progress';
import { useAutoUpdatesStore } from '../../../../store/autoUpdatesStore';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import type {
  AutoUpdateEvent,
  UpdateDownloadProgress,
  UpdateInfo,
} from '../../../../types/autoUpdates';

/** Check if we're in a desktop (Electron) environment */
function isDesktop(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    navigator.userAgent.includes('Electron') &&
    !!window.electronAPI?.autoUpdates
  );
}

export function UpdatesSettingsTab() {
  const { state, isLoading, setState, handleEvent } = useAutoUpdatesStore();

  // Fetch initial state on mount
  useEffect(() => {
    if (!isDesktop()) return;

    const fetchState = async () => {
      try {
        const initialState = await window.electronAPI!.autoUpdates.getState();
        setState(initialState);
      } catch (error) {
        console.error('[Updates] Failed to fetch initial state:', error);
      }
    };

    fetchState();
  }, [setState]);

  // Subscribe to update events from main process
  useEffect(() => {
    if (!isDesktop()) return;

    const unsubscribe = window.electronAPI!.autoUpdates.onEvent(
      (event: AutoUpdateEvent) => {
        handleEvent(event);
      }
    );

    return unsubscribe;
  }, [handleEvent]);

  // Handle manual check for updates
  const handleCheckForUpdates = useCallback(async () => {
    if (!isDesktop()) return;

    try {
      const result = await window.electronAPI!.autoUpdates.check();
      if (result.state) {
        setState(result.state);
      }
    } catch (error) {
      console.error('[Updates] Check for updates failed:', error);
    }
  }, [setState]);

  // Web build: show message that updates are handled differently
  if (!isDesktop()) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border border-border bg-muted/30 p-4">
          <p className="text-sm text-muted-foreground">
            Auto-updates are only available in the desktop app.
          </p>
        </div>
      </div>
    );
  }

  const {
    status,
    currentVersion,
    errorMessage,
    isDisabled,
    lastCheckAt,
    updateInfo,
    downloadProgress,
  } = state;

  // Format last check time
  const formatLastCheck = () => {
    if (!lastCheckAt) return 'Never';
    const date = new Date(lastCheckAt);
    return date.toLocaleString();
  };

  return (
    <div className="space-y-4">
      {/* Current Version */}
      <div className="space-y-3">
        <div className="text-sm font-medium">Current Version</div>
        <div className="rounded-md border border-border bg-background p-3">
          <span className="text-sm font-mono" data-testid="current-version">
            {currentVersion || 'Loading...'}
          </span>
        </div>
      </div>

      {/* Update Status */}
      <div className="space-y-3">
        <div className="text-sm font-medium">Update Status</div>
        <div
          className="rounded-md border border-border bg-background p-4"
          data-testid="update-status-container"
        >
          {isDisabled ? (
            <div className="flex items-center gap-3">
              <Ban className="h-5 w-5 text-muted-foreground" />
              <div className="grid gap-0.5">
                <span className="text-sm font-medium">Auto-updates disabled</span>
                <span className="text-xs text-muted-foreground">
                  Set via SQL_STUDIO_DISABLE_AUTO_UPDATE environment variable
                </span>
              </div>
            </div>
          ) : isLoading ? (
            <div className="flex items-center gap-3">
              <RandomDotmSpinner size={20} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Loading...</span>
            </div>
          ) : (
            <StatusDisplay
              status={status}
              errorMessage={errorMessage}
              lastCheckAt={formatLastCheck()}
              updateInfo={updateInfo}
              downloadProgress={downloadProgress}
              onRetry={handleCheckForUpdates}
            />
          )}
        </div>
      </div>

      {/* Check for Updates Button */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          Last checked: {formatLastCheck()}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCheckForUpdates}
          disabled={isDisabled || status === 'checking' || status === 'downloading'}
          data-testid="check-for-updates-button"
        >
          {status === 'checking' ? (
            <>
              <RandomDotmSpinner size={16} className="mr-2" />
              Checking...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Check for updates
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

/** Format bytes to human-readable string */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/** Display component for different update statuses */
function StatusDisplay({
  status,
  errorMessage,
  lastCheckAt,
  updateInfo,
  downloadProgress,
  onRetry,
}: {
  status: string;
  errorMessage: string | null;
  lastCheckAt: string;
  updateInfo: UpdateInfo | null;
  downloadProgress: UpdateDownloadProgress | null;
  onRetry: () => void;
}) {
  switch (status) {
    case 'idle':
      return (
        <div className="flex items-center gap-3">
          <RefreshCw className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            Click "Check for updates" to check for new versions
          </span>
        </div>
      );

    case 'checking':
      return (
        <div className="flex items-center gap-3">
          <RandomDotmSpinner size={20} className="text-primary" />
          <span className="text-sm">Checking for updates...</span>
        </div>
      );

    case 'available':
      return (
        <div className="flex items-center gap-3">
          <Download className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <div className="grid gap-0.5">
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
              Update available: v{updateInfo?.version ?? 'unknown'}
            </span>
            <span className="text-xs text-muted-foreground">
              Download starting...
            </span>
          </div>
        </div>
      );

    case 'downloading':
      return (
        <div className="space-y-2" data-testid="download-progress-container">
          <div className="flex items-center gap-3">
            <ArrowDownToLine className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-pulse" />
            <div className="flex-1 grid gap-0.5">
              <span className="text-sm font-medium">
                Downloading v{updateInfo?.version ?? 'update'}...
              </span>
              <span className="text-xs text-muted-foreground" data-testid="download-progress-text">
                {downloadProgress
                  ? `${formatBytes(downloadProgress.bytesTransferred)} / ${formatBytes(downloadProgress.totalBytes)}`
                  : 'Preparing download...'}
              </span>
            </div>
            <span
              className="text-sm font-mono text-muted-foreground"
              data-testid="download-progress-percent"
            >
              {downloadProgress?.percent?.toFixed(0) ?? 0}%
            </span>
          </div>
          <Progress
            value={downloadProgress?.percent ?? 0}
            className="h-2"
            data-testid="download-progress-bar"
          />
        </div>
      );

    case 'downloaded':
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
            <div className="grid gap-0.5">
              <span className="text-sm font-medium text-green-700 dark:text-green-300">
                Update ready: v{updateInfo?.version ?? 'unknown'}
              </span>
              <span className="text-xs text-muted-foreground">
                Restart to apply the update
              </span>
            </div>
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={async () => {
              try {
                await window.electronAPI?.autoUpdates.install();
              } catch (error) {
                console.error('[Updates] Install failed:', error);
              }
            }}
            className="w-full"
            data-testid="install-update-button"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Restart Now
          </Button>
        </div>
      );

    case 'up-to-date':
      return (
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
          <div className="grid gap-0.5">
            <span className="text-sm font-medium text-green-700 dark:text-green-300">
              You're up to date
            </span>
            <span className="text-xs text-muted-foreground">
              Last checked: {lastCheckAt}
            </span>
          </div>
        </div>
      );

    case 'error':
      return (
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="grid gap-1 min-w-0 flex-1">
              <span className="text-sm font-medium text-red-700 dark:text-red-300">
                Error checking for updates
              </span>
              {errorMessage && (
                <div className="max-h-24 overflow-y-auto rounded border border-border bg-muted/30 p-2">
                  <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-all font-mono">
                    {errorMessage}
                  </pre>
                </div>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="w-full"
            data-testid="retry-check-button"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </div>
      );

    default:
      return (
        <div className="flex items-center gap-3">
          <RefreshCw className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Status: {status}</span>
        </div>
      );
  }
}
